import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Group, Stack, Text, Title, UnstyledButton} from '@mantine/core';
import React, {useCallback, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {getOrCreateDraftState} from 'llm/chat-library/ChatDraftState';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {NewChatInput} from '../ChatInput';
import type {NewChatInputHandle} from '../ChatInput';
import type {CreateChatResult} from './ChatRouter';
import {getDefaultAgentType} from './ChatTypeUtil';

const TEST_COMPONENT = 'AIOnboardingCompletedScreen';
const DEFAULT_TITLE = "You're all set";
const DEFAULT_SUBTITLE = 'Benchling AI is now tuned to your work. Ask anything below.';

/** A single completion-page prompt card. */
export interface OnboardingCompletedCard {
  label: string;
  prompt: string;
  /**
   * Per-card icon, overriding the screen-level {@link AIOnboardingCompletedScreenProps.icon}. Used by
   * the Gemini path, where each suggestion can come from a different progressive-disclosure category and
   * should show that category's icon.
   */
  icon?: BenchlingIconName;
  /** Fired (e.g. for analytics) when the card is chosen, before the chat is submitted. */
  onSelect: () => void;
}

interface AIOnboardingCompletedScreenProps {
  /** Default card icon, used for cards that don't specify their own (e.g. the selected category's icon). */
  icon?: BenchlingIconName;
  /** The prompt options presented as cards. Omit/empty to show the completion page with no prompts. */
  cards?: ReadonlyArray<OnboardingCompletedCard>;
  /** Where these cards came from; recorded on the completion analytics event. */
  path?: 'category' | 'gemini';
  /** Called with the created chat once the first chat has been submitted. */
  onSubmitFirstChat: (result: CreateChatResult) => void;
  /** Called when no agent type is available, so submission can't happen (falls back to a skip). */
  onNoAgentAvailable: () => void;
  title?: string;
  subtitle?: string;
}

/**
 * The "You're all set" onboarding completion page. Renders a celebratory hero, the chosen prompt
 * options as cards (if any), and a chat input pinned to the bottom.
 *
 * The cards reuse the progressive-disclosure interaction: hovering a card pre-fills the chat input with
 * its prompt, and clicking it submits that prompt as the user's first chat (via {@link NewChatInput}'s
 * imperative handle, mirroring `WelcomeToChatPanel`). The user can also ignore the cards and type their
 * own first message. Each card shows its own {@link OnboardingCompletedCard.icon} when set, otherwise
 * the screen-level {@link icon}. With no cards (e.g. when Gemini can't mint suggestions) the page still
 * shows the hero and input so the user can type their first message.
 *
 * Renders inside the onboarding shell provided by the host, so it adds no chrome of its own.
 */
export function AIOnboardingCompletedScreen({
  icon,
  cards = [],
  path,
  onSubmitFirstChat,
  onNoAgentAvailable,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
}: AIOnboardingCompletedScreenProps): JSX.Element | null {
  const {classes} = useStyles();
  const newChatInputRef = useRef<NewChatInputHandle>(null);
  const [isPreviewText, setIsPreviewText] = useState(false);
  // The submission origin read by handleCreateChat: card clicks set it to `path`; typed messages keep
  // the default 'typed'. onCreateChat carries no per-submit path, so we stash it here.
  const submissionPathRef = useRef<'category' | 'gemini' | 'typed'>('typed');
  // Guards against a second card click creating a second chat: submitWithText does not consult the
  // input's own isSubmitting state.
  const isSubmittingRef = useRef(false);

  // The bottom input relies on a persisted draft (with an agent type) to submit. During onboarding no
  // route has called openDraftChat, so seed one here. With no agent available there's nothing to submit
  // to, so fall back to a skip rather than stranding the user on dead cards.
  const [hasAgent] = useState(() => getDefaultAgentType() != null);
  useOnMount(() => {
    if (!hasAgent) {
      onNoAgentAvailable();
      return;
    }
    getOrCreateDraftState(null);
  });

  const handleHoverCard = useCallback((prompt: string | null) => {
    if (prompt != null) {
      newChatInputRef.current?.setPreviewText(prompt);
      setIsPreviewText(true);
    } else {
      newChatInputRef.current?.clearPreviewText();
      setIsPreviewText(false);
    }
  }, []);

  const handleSelectCard = useCallback(
    (card: OnboardingCompletedCard) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      submissionPathRef.current = path ?? 'typed';
      card.onSelect();
      setIsPreviewText(false);
      // submitWithText handles its own errors; nothing to await here.
      newChatInputRef.current?.submitWithText(card.prompt).catch(() => {});
    },
    [path]
  );

  const handleCreateChat = useCallback(
    (result: CreateChatResult) => {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.COMPLETED_ONBOARDING_FIRST_CHAT, {
        path: submissionPathRef.current,
        chatSessionId: result.chatSessionId,
      });
      onSubmitFirstChat(result);
    },
    [onSubmitFirstChat]
  );

  if (!hasAgent) {
    return null;
  }

  return (
    <Box className={classes.root} {...getTestProps({component: TEST_COMPONENT, element: 'root'})}>
      <Stack className={classes.topZone} align="center" spacing="xl">
        <Stack align="center" spacing="sm">
          <BenchlingIcon icon="check-circle-outline" aria-hidden={true} className={classes.heroIcon} />
          <Title order={2} className={classes.title}>
            {title}
          </Title>
          <Text c="dimmed" className={classes.subtitle}>
            {subtitle}
          </Text>
        </Stack>
        {cards.length > 0 && (
          <Group spacing="sm" position="center" className={classes.cards} align="stretch">
            {cards.map((card) => {
              const cardIcon = card.icon ?? icon;
              return (
                <UnstyledButton
                  key={card.label}
                  className={classes.card}
                  onMouseEnter={() => {
                    handleHoverCard(card.prompt);
                  }}
                  onMouseLeave={() => {
                    handleHoverCard(null);
                  }}
                  onFocus={() => {
                    handleHoverCard(card.prompt);
                  }}
                  onBlur={() => {
                    handleHoverCard(null);
                  }}
                  onClick={() => {
                    handleSelectCard(card);
                  }}
                  {...getTestProps({component: TEST_COMPONENT, element: 'card'})}
                >
                  {cardIcon != null && (
                    <BenchlingIcon icon={cardIcon} aria-hidden={true} className={classes.cardIcon} />
                  )}
                  <Text className={classes.cardLabel}>{card.label}</Text>
                </UnstyledButton>
              );
            })}
          </Group>
        )}
      </Stack>
      <Box className={classes.inputZone}>
        {/* Draft state (including agentType) is seeded on mount above. */}
        <NewChatInput ref={newChatInputRef} onCreateChat={handleCreateChat} isPreviewText={isPreviewText} />
      </Box>
    </Box>
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Hero + cards, centered in the space above the bottom-pinned input.
  topZone: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    justifyContent: 'center',
  },
  heroIcon: {
    width: 32,
    height: 32,
    fontSize: 32,
    color: theme.colors.blue[6],
  },
  title: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 700,
    lineHeight: 1.3,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
  },
  cards: {
    flexWrap: 'nowrap',
    alignSelf: 'stretch',
  },
  card: {
    flex: 1,
    minWidth: 0,
    minHeight: 120,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    borderRadius: theme.radius.lg,
    backgroundColor: theme.colors.blue[0],
    color: theme.colors.blue[8],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
  cardIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    color: theme.colors.blue[6],
  },
  cardLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.colors.blue[8],
  },
  inputZone: {
    flex: 'none',
  },
}));
